import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import { saveAs } from "file-saver";
import RotateDevice from "../../components/RotateDevice/RotateDevice";
import "./GestionePresenze.css";
import Lf23Loader from "../../components/Lf23Loader";

/**
 * Dashboard "Gestione Presenze" — centri diurni accolti CSRD e CSO.
 * DATI REALI: previsto da Interacta + consuntivo dal DB mobile, incrociati dal
 * backend (GET /api/gestione-presenze?anno&mese&centro).
 *
 * DUE TAB, UNA SOLA UI: i due centri hanno filiere dati separate ma producono dati
 * della stessa forma, quindi il tab cambia solo il parametro `centro` della fetch —
 * matrice, calendario, KPI e grafici sono gli stessi. Il centro attivo vive nella
 * querystring (?centro=cso) così il link è condivisibile e il refresh lo conserva.
 *
 * Filtri "a scomparsa" con APPLICA esplicito (draft -> applied). Accolto multi-select.
 * Dettaglio giornata come modale centrale.
 *
 * INSERIMENTO MANUALE: in matrice una cella vuota di un giorno non futuro = accolto non
 * nel piano di quel giorno (tipico: contratto caricato dopo la pubblicazione). Cliccandola
 * si registra a mano presenza e trasporti; le righe manuali (✎) si possono poi modificare
 * o eliminare.
 * CORREZIONE: dal dettaglio di una cella GIÀ compilata (manuale o registrata dall'operatore
 * via app) l'ufficio può aprire "Modifica" per correggere stato/servizi (es. un trasporto
 * selezionato per errore). Solo le righe manuali si possono anche eliminare: quelle
 * dell'operatore restano, ma sempre correggibili. La correzione non si riflette su
 * Interacta (vive solo su questa dashboard).
 */

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

/* Su un giorno chiuso contano solo le celle "fuori chiusura": presenze extra sbloccate
   dall'operatore o presenze inserite a mano. Stessa regola del backend e dell'export. */
const fuoriChiusura = (a) => !!(a && (a.extra || a.manuale));

/** "YYYY-MM-DD" di oggi in ora locale: niente inserimenti manuali nel futuro. */
const oggiISO = () => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
};

// I due centri diurni. Qui stanno solo le etichette: il numero di community per i
// testi arriva dal backend nel payload (data.centro), così non è cablato due volte.
const CENTRI = {
    csrd: { label: "CSRD", subtitle: "Centro diurno accolti CSRD" },
    cso: { label: "CSO", subtitle: "Centro diurno accolti CSO" },
};
const CENTRO_DEFAULT = "csrd";

const GIORNI = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

// ---- icone line-style (coerenti col resto del progetto) ----
const svg = (children) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const ICONS = {
    calendar: svg(<><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>),
    check: svg(<><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>),
    x: svg(<><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></>),
    percent: svg(<><line x1="19" y1="5" x2="5" y2="19" /><circle cx="6.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="17.5" r="2.5" /></>),
    lock: svg(<><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>),
    truck: svg(<><rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></>),
    clipboard: svg(<><rect x="3" y="3" width="18" height="18" rx="2" /><polyline points="9 12 11 14 15 10" /></>),
    half: svg(<><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>),
};

const ESITO_LETTERA = { presente: "P", assente: "A", chiuso: "C" };
const ESITO_LABEL = { presente: "Presente", assente: "Assente", chiuso: "Chiuso", na: "Da registrare" };

function buildMonthOptions(count = 12) {
    const now = new Date();
    const opts = [];
    let y = now.getFullYear();
    let m = now.getMonth() + 1;
    for (let i = 0; i < count; i++) {
        opts.push({ value: `${y}-${m}`, label: `${MESI[m - 1]} ${y}` });
        m--; if (m < 1) { m = 12; y--; }
    }
    return opts;
}
const MONTH_OPTIONS = buildMonthOptions(12);

function heatClass(p, a) {
    const tot = p + a;
    if (tot === 0) return null;
    const r = p / tot;
    if (r >= 0.9) return "h1";
    if (r >= 0.75) return "h2";
    if (r >= 0.5) return "h3";
    return "h4";
}

// segmenti di un donut (r=15.9 → circonferenza ≈ 100)
function donutSegments(parts) {
    const total = parts.reduce((s, p) => s + p.value, 0);
    let acc = 0;
    return parts.map((p) => {
        const pct = total > 0 ? (p.value / total) * 100 : 0;
        const seg = { ...p, dasharray: `${pct} ${100 - pct}`, dashoffset: 25 - acc };
        acc += pct;
        return seg;
    });
}

const TRASP_LET = { trasporto_andata: "A", trasporto_ritorno: "R", trasporto_individualizzato: "I" };
const TRASPORTI_FORM = [["trasporto_andata", "Andata"], ["trasporto_ritorno", "Ritorno"], ["trasporto_individualizzato", "Individualizzato"]];

// Granularità di default in base alla larghezza pagina: <480 Giorno, <1024 Settimana, ≥1024 Mese
const granForWidth = (w) => (w < 480 ? "giorno" : w < 1024 ? "settimana" : "mese");

const GestionePresenze = () => {
    const navigate = useNavigate();
    const CURR = useMemo(() => { const n = new Date(); return { anno: n.getFullYear(), mese: n.getMonth() + 1 }; }, []);

    // Il centro attivo è DERIVATO dall'URL, non tenuto in uno useState parallelo:
    // una sola sorgente di verità, e back/forward del browser funzionano gratis.
    // Un valore sconosciuto in querystring degrada al default invece di rompere.
    const [searchParams, setSearchParams] = useSearchParams();
    const qsCentro = searchParams.get("centro");
    const centro = Object.prototype.hasOwnProperty.call(CENTRI, qsCentro) ? qsCentro : CENTRO_DEFAULT;

    // stato APPLICATO (guida fetch + rendering)
    const [anno, setAnno] = useState(CURR.anno);
    const [mese, setMese] = useState(CURR.mese);
    const [accoltiSel, setAccoltiSel] = useState([]); // id[] applicati

    // stato DRAFT (pannello filtri, applicato solo con "Applica")
    const [draftPeriodo, setDraftPeriodo] = useState(`${CURR.anno}-${CURR.mese}`);
    const [draftAccolti, setDraftAccolti] = useState([]); // id[]

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [view, setView] = useState("mat");   // 'mat' | 'cal' — default matrice (stile GECOS)
    const [gran, setGran] = useState(() => granForWidth(window.innerWidth));  // 'mese' | 'settimana' | 'giorno' — default responsive: <480 Giorno, <1024 Settimana, ≥1024 Mese
    const [focusDay, setFocusDay] = useState(new Date().getDate());
    const [showFilters, setShowFilters] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [dayModal, setDayModal] = useState(null); // data del giorno aperto nel modale
    const [barTip, setBarTip] = useState(null); // tooltip barra andamento: { x, y, t }
    const [totTip, setTotTip] = useState(null); // tooltip colonna Tot: { x, y, nome, s }
    const [esportando, setEsportando] = useState(false);
    const [hlRow, setHlRow] = useState(null); // riga accolto evidenziata nella matrice
    const [cellModal, setCellModal] = useState(null); // { accId, ds } — dettaglio cella matrice
    const [manForm, setManForm] = useState(null); // form inserimento/modifica manuale: { nuovo, stato, servizi[], saving, errore }
    const [isFullscreen, setIsFullscreen] = useState(false);

    const fetchData = useCallback(() => {
        setLoading(true);
        setError(null);
        axios
            .get(`${API_URL}/gestione-presenze`, { params: { anno, mese, centro } })
            .then((r) => setData(r.data))
            .catch((e) => setError(e.response?.data?.error || e.message))
            .finally(() => setLoading(false));
    }, [anno, mese, centro]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Cambio centro: anno/mese restano (è il confronto che si vuole fare), mentre gli
    // stati che referenziano ACCOLTI o GIORNI del centro precedente vanno azzerati —
    // gli id accolto sono post-id di anagrafiche diverse e non esistono nell'altro
    // centro. L'effect su [data] pota già accoltiSel/draftAccolti quando arriva la
    // risposta, ma azzerare qui è immediato e non dipende da un effetto collaterale.
    const changeCentro = useCallback((next) => {
        if (next === centro) return;
        setSearchParams(next === CENTRO_DEFAULT ? {} : { centro: next });
        setAccoltiSel([]);
        setDraftAccolti([]);
        setHlRow(null);
        setCellModal(null);
        setManForm(null);
        setDayModal(null);
        setShowInfo(false);
    }, [centro, setSearchParams]);

    // pota gli id non presenti nel mese caricato (applicati + draft)
    useEffect(() => {
        if (!data) return;
        const valid = new Set(data.accoltiList.map((a) => a.id));
        setAccoltiSel((prev) => prev.filter((id) => valid.has(id)));
        setDraftAccolti((prev) => prev.filter((id) => valid.has(id)));
    }, [data]);

    // tiene focusDay (matrice settimana/giorno) entro i giorni del mese caricato
    useEffect(() => { if (data) setFocusDay((f) => Math.min(Math.max(1, f), data.periodo.giorniMese)); }, [data]);

    // Responsive: cambia granularità al cambio di fascia di larghezza, senza ricaricare.
    // Scatta SOLO quando si cambia fascia (non a ogni resize), così non sovrascrive una scelta
    // manuale finché si resta nella stessa fascia. <480 → Giorno, <1024 → Settimana, ≥1024 → Mese.
    useEffect(() => {
        let lastAuto = granForWidth(window.innerWidth);
        const onResize = () => {
            const next = granForWidth(window.innerWidth);
            if (next !== lastAuto) {
                lastAuto = next;
                setGran(next);
            }
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    // ESC esce dal fullscreen
    useEffect(() => {
        const onEsc = (e) => { if (e.key === "Escape" && isFullscreen) setIsFullscreen(false); };
        document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, [isFullscreen]);

    // I totali del tooltip sono una fotografia del periodo mostrato: se cambia la
    // granularità (o il mese, o i filtri) il tooltip aperto per click su touch
    // resterebbe con numeri vecchi — meglio chiuderlo.
    useEffect(() => { setTotTip(null); }, [gran, focusDay, anno, mese, accoltiSel]);

    const applyFilters = () => {
        const [y, m] = draftPeriodo.split("-").map(Number);
        setAnno(y); setMese(m);
        setAccoltiSel(draftAccolti);
    };
    const resetFilters = () => {
        setDraftPeriodo(`${CURR.anno}-${CURR.mese}`);
        setDraftAccolti([]);
        setAnno(CURR.anno); setMese(CURR.mese);
        setAccoltiSel([]);
    };
    const goMonth = (delta) => {
        let m = mese + delta, y = anno;
        if (m < 1) { m = 12; y--; }
        if (m > 12) { m = 1; y++; }
        setMese(m); setAnno(y);
        setDraftPeriodo(`${y}-${m}`); // tiene il pannello allineato
    };
    // navigazione matrice: passo per granularità (mese/settimana/giorno), con rollover di mese
    const stepNav = (delta) => {
        if (gran === "mese" || !data) { goMonth(delta); return; }
        const N = data.periodo.giorniMese;
        const nd = focusDay + delta * (gran === "settimana" ? 7 : 1);
        if (nd < 1) { goMonth(-1); setFocusDay(99); }        // 99 → clampato all'ultimo giorno dall'effect
        else if (nd > N) { goMonth(1); setFocusDay(1); }
        else setFocusDay(nd);
    };
    const goToday = () => {
        const n = new Date();
        setAnno(n.getFullYear()); setMese(n.getMonth() + 1); setFocusDay(n.getDate());
    };

    /* Export Excel di quello che si sta guardando: vista (Dettaglio = matrice accolti x
       giorni, Aggregato = una riga per giornata), giorni a video secondo la granularità
       e accolti filtrati. Il report viene ricostruito lato backend con la stessa
       buildReport() che serve la pagina, così non si duplicano in due posti le regole di
       incrocio previsto/consuntivo; qui si manda solo cosa mostrare.
       Costa una rilettura di Interacta + DB mobile (qualche secondo), da cui il "disabled". */
    const esportaExcel = async () => {
        setEsportando(true);
        try {
            const res = await axios.post(
                `${API_URL}/gestione-presenze/export`,
                // in Aggregato la granularità non si applica (il calendario è sempre
                // mensile): giorni vuoto = tutto il mese
                { anno, mese, centro, accolti: accoltiSel, vista: view, giorni: view === "mat" ? visDays : [] },
                { responseType: "blob" }
            );
            const nome = res.headers["content-disposition"]?.match(/filename=([^;]+)/)?.[1]?.trim();
            saveAs(res.data, nome || `presenze_${centro}_${anno}-${String(mese).padStart(2, "0")}.xlsx`);
        } catch (e) {
            console.error("Errore export Excel presenze:", e);
            alert("Non è stato possibile generare il file Excel.");
        } finally {
            setEsportando(false);
        }
    };

    /* ---- Inserimento manuale ----
       Dopo ogni scrittura si ricarica il mese: il report lo ricostruisce il backend,
       così KPI, Tot ed export vedono subito la riga nuova con le stesse regole. */
    const apriInserimento = (accId, ds) => {
        setCellModal({ accId, ds });
        setManForm({ nuovo: true, stato: "presente", servizi: [], saving: false, errore: null });
    };
    const apriModifica = (a) => {
        setManForm({ nuovo: false, stato: a.esito, servizi: [...a.serviziEffettuati], saving: false, errore: null });
    };
    const chiudiCellModal = () => { setCellModal(null); setManForm(null); };

    const salvaManuale = async () => {
        if (!cellModal || !manForm) return;
        setManForm((f) => ({ ...f, saving: true, errore: null }));
        const body = { centro, data: cellModal.ds, accoltoId: cellModal.accId, stato: manForm.stato, servizi: manForm.servizi };
        try {
            if (manForm.nuovo) await axios.post(`${API_URL}/gestione-presenze/manuale`, body, { headers: authHeaders() });
            else await axios.put(`${API_URL}/gestione-presenze/manuale`, body, { headers: authHeaders() });
            chiudiCellModal();
            fetchData();
        } catch (e) {
            setManForm((f) => ({ ...f, saving: false, errore: e.response?.data?.error || e.message }));
        }
    };

    const eliminaManuale = async () => {
        if (!cellModal) return;
        if (!window.confirm("Eliminare la presenza inserita a mano?")) return;
        try {
            await axios.delete(`${API_URL}/gestione-presenze/manuale`, {
                headers: authHeaders(),
                params: { centro, data: cellModal.ds, accoltoId: cellModal.accId },
            });
            chiudiCellModal();
            fetchData();
        } catch (e) {
            alert(e.response?.data?.error || "Non è stato possibile eliminare la presenza.");
        }
    };

    const mm = String(mese).padStart(2, "0");
    const meseLabel = `${MESI[mese - 1]} ${anno}`;

    const selSet = useMemo(() => new Set(accoltiSel), [accoltiSel]);
    const filterAccolti = useCallback((arr) => (selSet.size ? arr.filter((a) => selSet.has(a.id)) : arr), [selSet]);
    const singleAccolto = accoltiSel.length === 1 ? accoltiSel[0] : null;

    /* Giorni-colonna della matrice, in base alla granularità (Mese/Settimana/Giorno).
       Calcolati qui e non dentro il render perché servono anche all'export, che deve
       produrre esattamente il periodo che si sta guardando. */
    const visDays = useMemo(() => {
        if (!data) return [];
        const N = data.periodo.giorniMese;
        const fd = Math.min(Math.max(1, focusDay), N);
        let days;
        if (gran === "giorno") days = [fd];
        else if (gran === "settimana") {
            const wd0 = (new Date(anno, mese - 1, fd).getDay() + 6) % 7;
            days = [];
            for (let i = 0; i < 7; i++) { const d = fd - wd0 + i; if (d >= 1 && d <= N) days.push(d); }
        } else days = Array.from({ length: N }, (_, i) => i + 1);
        // Sabato e domenica restano sempre a calendario, anche senza piano (richiesta
        // del cliente): la colonna vuota dice "quel giorno non c'era nessuno".
        return days;
    }, [data, gran, focusDay, anno, mese]);

    const dayCounts = useCallback((g) => {
        const arr = filterAccolti(g.accolti);
        let p = 0, a = 0;
        for (const x of arr) { if (x.esito === "presente") p++; else if (x.esito === "assente") a++; }
        return { p, a };
    }, [filterAccolti]);

    // ---- render helper (data garantito non-null) ----
    const renderContent = () => {
        const giorniByDate = {};
        for (const g of data.giorni) giorniByDate[g.data] = g;

        // "Interacta 143" per il CSO, "Interacta 135" per il CSRD: il numero arriva dal
        // backend. Se rispondesse una versione precedente del backend, resta "Interacta".
        const fonteLabel = data.centro ? `Interacta ${data.centro.communityId}` : "Interacta";

        const accoltoOptions = data.accoltiList.map((a) => ({ value: a.id, label: a.nome }));
        const nomeById = (id) => data.accoltiList.find((a) => a.id === id)?.nome || id;

        // badge di contesto
        let badgeText = meseLabel;
        if (accoltiSel.length === 1) badgeText = nomeById(accoltiSel[0]);
        else if (accoltiSel.length > 1) badgeText = `${accoltiSel.length} accolti`;

        // ---- KPI ----
        const buildKpiAgg = () => {
            let presenze = 0, assenze = 0, andata = 0, ritorno = 0, indiv = 0;
            let presenzeExtra = 0, assenzeExtra = 0;
            const giorniAperti = data.giorni.filter((g) => !g.chiusura).length;
            const giorniChiusura = data.giorni.filter((g) => g.chiusura).length;
            const giornateConf = data.giorni.filter((g) => g.confermato).length;
            // i giorni "senza piano" (solo presenze a mano) non hanno un post da confermare
            const conPiano = data.giorni.filter((g) => !g.senzaPiano).length;
            for (const g of data.giorni) {
                for (const a of filterAccolti(g.accolti)) {
                    // giorni aperti: conteggio normale; giorni chiusi: solo extra e manuali
                    if (g.chiusura && !fuoriChiusura(a)) continue;
                    if (a.esito === "presente") { presenze++; if (a.extra) presenzeExtra++; }
                    else if (a.esito === "assente") { assenze++; if (a.extra) assenzeExtra++; }
                    for (const s of a.serviziEffettuati) {
                        if (s === "trasporto_andata") andata++;
                        else if (s === "trasporto_ritorno") ritorno++;
                        else if (s === "trasporto_individualizzato") indiv++;
                    }
                }
            }
            const tot = presenze + assenze;
            const tasso = tot > 0 ? Math.round((presenze / tot) * 100) : 0;
            const assPerc = tot > 0 ? Math.round((assenze / tot) * 100) : 0;
            const confPerc = conPiano > 0 ? Math.round((giornateConf / conPiano) * 100) : 0;
            return [
                ["primary", "calendar", "Giorni aperti", String(giorniAperti), `di ${data.periodo.giorniMese} · ${conPiano} con piano`],
                ["success", "check", "Presenze", String(presenze), presenzeExtra > 0 ? `di cui ${presenzeExtra} extra` : ""],
                ["danger", "x", "Assenze", String(assenze), `tasso assenza ${assPerc}%${assenzeExtra > 0 ? ` · ${assenzeExtra} extra` : ""}`],
                ["info", "percent", "Tasso presenza", `${tasso}%`, ""],
                ["slate", "lock", "Giorni chiusura", String(giorniChiusura), ""],
                ["warning", "truck", "Trasporti", String(andata + ritorno + indiv), `A ${andata} · R ${ritorno} · I ${indiv}`],
                ["success", "clipboard", "Giornate confermate", `${confPerc}%`, `${giornateConf} di ${conPiano}`],
            ];
        };
        /* Totali di UN accolto. Unica fonte per le KPI del singolo accolto e per la
           colonna "Tot" della matrice: cosi' i due numeri non possono divergere.
           `dateSet` limita il conteggio a un sottoinsieme di giorni: le KPI lo omettono
           (sempre il mese), la colonna Tot passa i giorni visibili, cosi' il totale
           segue la granularita' scelta (Mese / Settimana / Giorno). */
        const statsAccolto = (id, dateSet) => {
            let previsti = 0, presenze = 0, assenze = 0, chiusi = 0, mezze = 0, andata = 0, ritorno = 0, indiv = 0;
            let presenzeExtra = 0, assenzeExtra = 0;
            for (const g of data.giorni) {
                if (dateSet && !dateSet.has(g.data)) continue;
                const a = g.accolti.find((x) => x.id === id);
                if (!a) continue;
                if (g.chiusura && !fuoriChiusura(a)) { chiusi++; continue; } // giorno chiuso senza extra/manuale per questo accolto
                if (!g.chiusura) previsti++; // gli extra su giorno chiuso non erano "previsti"
                if (a.esito === "presente") { presenze++; if (a.extra) presenzeExtra++; }
                else if (a.esito === "assente") { assenze++; if (a.extra) assenzeExtra++; }
                if (a.presenza_mezza) mezze++;
                for (const s of a.serviziEffettuati) {
                    if (s === "trasporto_andata") andata++;
                    else if (s === "trasporto_ritorno") ritorno++;
                    else if (s === "trasporto_individualizzato") indiv++;
                }
            }
            const tasso = presenze + assenze > 0 ? Math.round((presenze / (presenze + assenze)) * 100) : 0;
            return { previsti, presenze, assenze, chiusi, mezze, andata, ritorno, indiv, presenzeExtra, assenzeExtra, tasso };
        };
        const buildKpiAccolto = (id) => {
            const s = statsAccolto(id);
            return [
                ["primary", "calendar", "Giorni previsti", String(s.previsti), ""],
                ["success", "check", "Presenze", String(s.presenze), `tasso ${s.tasso}%${s.presenzeExtra > 0 ? ` · ${s.presenzeExtra} extra` : ""}`],
                ["danger", "x", "Assenze", String(s.assenze), s.assenzeExtra > 0 ? `di cui ${s.assenzeExtra} extra` : ""],
                ["warning", "truck", "Trasporti usati", String(s.andata + s.ritorno + s.indiv), `A ${s.andata} · R ${s.ritorno}${s.indiv ? ` · I ${s.indiv}` : ""}`],
                ["slate", "lock", "Giorni chiusura", String(s.chiusi), ""],
                ["info", "half", "Mezze giornate", String(s.mezze), ""],
            ];
        };
        const kpiData = singleAccolto ? buildKpiAccolto(singleAccolto) : buildKpiAgg();

        // ---- CALENDARIO ----
        const calCells = [];
        for (let i = 0; i < data.periodo.offsetLun; i++) calCells.push(<div key={`e${i}`} className="cal-cell empty" />);
        for (let d = 1; d <= data.periodo.giorniMese; d++) {
            const ds = `${anno}-${mm}-${String(d).padStart(2, "0")}`;
            const g = giorniByDate[ds];
            if (!g) { calCells.push(<div key={d} className="cal-cell nodata"><div className="cal-daynum">{d}</div></div>); continue; }
            if (g.chiusura) {
                calCells.push(
                    <div key={d} className="cal-cell closed">
                        <div className="cal-daynum">{d}</div>
                        <span className="mini-badge mb-chiuso">Chiuso{g.motivo_chiusura ? ` — ${g.motivo_chiusura}` : ""}</span>
                    </div>
                );
                continue;
            }
            if (singleAccolto) {
                const a = g.accolti.find((x) => x.id === singleAccolto);
                if (!a) { calCells.push(<div key={d} className="cal-cell nodata"><div className="cal-daynum">{d}</div></div>); continue; }
                const lett = ESITO_LETTERA[a.esito];
                calCells.push(
                    <div key={d} className="cal-cell" onClick={() => setDayModal(ds)}>
                        <div className="cal-daynum">{d}</div>
                        {lett ? <div className={`cal-stato st-${lett}`}>{lett}</div> : <div className="cal-na">da registrare</div>}
                        {a.esito === "presente" && (
                            <div className="cal-transp">
                                <span className={a.serviziEffettuati.includes("trasporto_andata") ? "tr-ok" : "tr-no"}>A</span>
                                <span className={a.serviziEffettuati.includes("trasporto_ritorno") ? "tr-ok" : "tr-no"}>R</span>
                                {a.nota && <span className="note-dot" title={a.nota} />}
                            </div>
                        )}
                    </div>
                );
                continue;
            }
            const { p, a } = dayCounts(g);
            const heat = heatClass(p, a);
            calCells.push(
                <div key={d} className="cal-cell" onClick={() => setDayModal(ds)}>
                    {heat && <span className={`dot ${heat}`} />}
                    <div className="cal-daynum">{d}</div>
                    {p + a > 0 ? (
                        <div className="cal-summary"><span className="pres">{p}</span> / <span className="ass">{a}</span></div>
                    ) : (
                        <div className="cal-na">da registrare</div>
                    )}
                    {g.senzaPiano
                        ? <span className="mini-badge mb-manuale">senza piano</span>
                        : !g.confermato && <span className="mini-badge mb-daconf">da confermare</span>}
                </div>
            );
        }

        // ---- MATRICE ----
        const matrixAccolti = selSet.size ? data.accoltiList.filter((a) => selSet.has(a.id)) : data.accoltiList;
        // i giorni-colonna arrivano dal useMemo `visDays` (condiviso con l'export)
        const fd = Math.min(Math.max(1, focusDay), data.periodo.giorniMese);
        let periodLabel = meseLabel;
        if (gran === "settimana" && visDays.length) periodLabel = `${visDays[0]}–${visDays[visDays.length - 1]} ${MESI[mese - 1]}`;
        else if (gran === "giorno") periodLabel = `${GIORNI[(new Date(anno, mese - 1, fd).getDay() + 6) % 7]} ${fd} ${MESI[mese - 1]} ${anno}`;
        const oggi = new Date();
        const isOggi = (d) => oggi.getFullYear() === anno && oggi.getMonth() + 1 === mese && oggi.getDate() === d;
        const oggiStr = oggiISO();

        /* Totali della colonna "Tot", calcolati una volta per render (non per cella).
           Sono limitati ai giorni a video: in vista Mese e' il mese intero, in Settimana
           la settimana, in Giorno quel solo giorno — cosi' il denominatore corrisponde
           sempre alle colonne che si stanno guardando. */
        const visDates = new Set(visDays.map((d) => `${anno}-${mm}-${String(d).padStart(2, "0")}`));
        const statsById = {};
        for (const a of matrixAccolti) statsById[a.id] = statsAccolto(a.id, visDates);
        // Il tooltip e' position:fixed (come quello delle barre): dentro .matrix-scroll,
        // che ha overflow, un popover in absolute verrebbe tagliato dal bordo della tabella.
        const openTotTip = (e, acc) => {
            const r = e.currentTarget.getBoundingClientRect();
            setTotTip({ x: r.left + r.width / 2, y: r.top - 6, id: acc.id, nome: acc.nome, periodo: periodLabel, s: statsById[acc.id] });
        };

        // ---- GRAFICI ----
        const trend = data.giorni.filter((g) => !g.chiusura).map((g) => { const c = dayCounts(g); return { data: g.data, p: c.p, a: c.a }; });
        const trendMax = Math.max(1, ...trend.map((t) => t.p + t.a));
        const assMap = new Map();
        for (const g of data.giorni) for (const a of filterAccolti(g.accolti)) if (a.esito === "assente") assMap.set(a.id, (assMap.get(a.id) || 0) + 1);
        const topAssenti = [...assMap.entries()].map(([id, v]) => ({ nome: nomeById(id), assenze: v })).sort((x, y) => y.assenze - x.assenze).slice(0, 5);
        const topMax = Math.max(1, ...topAssenti.map((t) => t.assenze));
        const trasp = { andata: 0, ritorno: 0, indiv: 0 };
        for (const g of data.giorni) for (const a of filterAccolti(g.accolti)) for (const s of a.serviziEffettuati) {
            if (s === "trasporto_andata") trasp.andata++;
            else if (s === "trasporto_ritorno") trasp.ritorno++;
            else if (s === "trasporto_individualizzato") trasp.indiv++;
        }
        const trasportiSeg = donutSegments([
            { label: "Andata", value: trasp.andata, color: "#2563EB" },
            { label: "Ritorno", value: trasp.ritorno, color: "#5A92D4" },
            { label: "Individualizzato", value: trasp.indiv, color: "#F59E0B" },
        ]);
        const chiusureColori = ["#64748B", "#94A3B8", "#CBD5E1", "#475569"];
        const chiusureSeg = donutSegments(data.charts.chiusure.map((c, i) => ({ label: c.motivo, value: c.count, color: chiusureColori[i % chiusureColori.length] })));

        return (
            <>
                {!data.consuntivoDisponibile && (
                    <div className="mock-note" style={{ marginBottom: 16 }}>
                        ⚠ Consuntivo non disponibile (DB app mobile non raggiungibile): sono mostrati solo i dati <strong>previsti</strong>.
                    </div>
                )}

                {/* CONTROLLI */}
                <div className="controls-section">
                    <button className={`toggle-filters-btn ${showFilters ? "active" : ""}`} onClick={() => setShowFilters(!showFilters)}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
                        {showFilters ? "Nascondi Filtri" : "Mostra Filtri"}
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
                    </button>

                    {showFilters && (
                        <div className="filter-panel">
                            <div className="filter-grid">
                                <div className="filter-group">
                                    <label>Periodo</label>
                                    <select value={draftPeriodo} onChange={(e) => setDraftPeriodo(e.target.value)}>
                                        {MONTH_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Accolto</label>
                                    <Select
                                        isMulti
                                        options={accoltoOptions}
                                        value={accoltoOptions.filter((o) => draftAccolti.includes(o.value))}
                                        onChange={(sel) => setDraftAccolti(sel ? sel.map((s) => s.value) : [])}
                                        placeholder="Tutti"
                                        noOptionsMessage={() => "Nessun accolto"}
                                        classNamePrefix="gp-select"
                                        closeMenuOnSelect={false}
                                    />
                                </div>
                            </div>
                            <div className="filter-actions">
                                <button className="filter-reset-btn" onClick={resetFilters}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /></svg>
                                    Reset
                                </button>
                                <button className="filter-apply-btn" onClick={applyFilters}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                                    Applica
                                </button>
                            </div>
                        </div>
                    )}

                </div>

                {/* KPI */}
                <div className="stats-grid-compact">
                    {kpiData.map(([variant, iconKey, label, value, sub], i) => (
                        <div className={`stat-card-compact stat-${variant}`} key={i}>
                            <div className="stat-icon-compact">{ICONS[iconKey]}</div>
                            <div className="stat-content-compact">
                                <p className="stat-label-compact">{label}</p>
                                <h3 className="stat-value-compact">{value}</h3>
                                {sub && <p className="stat-sub-compact">{sub}</p>}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Toggle vista + Aggiorna: tra le KPI e il calendario/matrice */}
                <div className="view-bar">
                    <div className="view-toggle-compact">
                        <button className={`view-btn-compact ${view === "mat" ? "active" : ""}`} onClick={() => setView("mat")}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>
                            <span>Dettaglio</span>
                        </button>
                        <button className={`view-btn-compact ${view === "cal" ? "active" : ""}`} onClick={() => setView("cal")}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                            <span>Aggregato</span>
                        </button>
                    </div>
                    <div className="view-bar-right">
                        <div className="results-badge">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                            {badgeText}
                        </div>
                        <button className="gp-refresh-btn" onClick={fetchData} disabled={loading} title="Ricarica i dati da Interacta e dal DB mobile">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 4v6h-6" /><path d="M1 20v-6h6" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>
                            <span>Aggiorna</span>
                        </button>
                        <button className="gp-export-btn" onClick={esportaExcel} disabled={esportando || data.giorni.length === 0} title={view === "mat" ? "Scarica in Excel la matrice come la stai vedendo (periodo, granularità e filtri)" : "Scarica in Excel il riepilogo per giornata del mese"}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                            <span>{esportando ? "Esportazione…" : "Esporta Excel"}</span>
                        </button>
                        <button className="gp-fs-btn" onClick={() => setIsFullscreen((v) => !v)} title={isFullscreen ? "Esci da tutto schermo" : "Tutto schermo"}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                {isFullscreen ? (
                                    <><polyline points="4 14 10 14 10 20" /><polyline points="20 10 14 10 14 4" /><line x1="14" y1="10" x2="21" y2="3" /><line x1="3" y1="21" x2="10" y2="14" /></>
                                ) : (
                                    <><polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" /></>
                                )}
                            </svg>
                        </button>
                    </div>
                </div>

                <div className={`gp-fs${isFullscreen ? " gp-fs-active" : ""}`}>
                    <RotateDevice active={isFullscreen} />
                    {isFullscreen && (
                        <div className="gp-fs-exit">
                            <button className="gp-fs-btn" onClick={() => setIsFullscreen(false)} title="Esci da tutto schermo">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 14 10 14 10 20" /><polyline points="20 10 14 10 14 4" /><line x1="14" y1="10" x2="21" y2="3" /><line x1="3" y1="21" x2="10" y2="14" /></svg>
                            </button>
                        </div>
                    )}
                {view === "cal" ? (
                    <div className="panel">
                        <div className="panel-head">
                            <h3>Aggregato — {meseLabel}{singleAccolto ? ` · ${nomeById(singleAccolto)}` : ""}</h3>
                            <div className="month-nav"><button onClick={() => goMonth(-1)}>‹</button><span>{meseLabel}</span><button onClick={() => goMonth(1)}>›</button></div>
                        </div>
                        <div className="panel-body">
                            <div className="cal-grid">{GIORNI.map((g) => <div className="cal-head" key={g}>{g}</div>)}</div>
                            <div className="cal-grid" style={{ marginTop: 8 }}>{calCells}</div>
                            {data.giorni.length === 0 && (
                                <div className="mock-note" style={{ marginTop: 16 }}>Nessun piano presenze pubblicato su {fonteLabel} per <strong>{meseLabel}</strong>.</div>
                            )}
                            <div className="legend">
                                {singleAccolto ? (
                                    <>
                                        <span><b className="st-P">P</b> Presente</span>
                                        <span><b className="st-A">A</b> Assente</span>
                                        <span><b className="st-C">C</b> Chiuso</span>
                                        <span>A/R = trasporto effettuato · <span className="note-dot" style={{ display: "inline-block" }} /> nota</span>
                                    </>
                                ) : (
                                    <>
                                        <span><span className="dot h1" style={{ position: "static" }} /> alta presenza</span>
                                        <span><span className="dot h3" style={{ position: "static" }} /> media</span>
                                        <span><span className="dot h4" style={{ position: "static" }} /> bassa</span>
                                        <span><span className="mini-badge mb-chiuso">Chiuso</span></span>
                                        <span><span className="mini-badge mb-daconf">da confermare</span></span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="panel">
                        <div className="panel-head mx-head">
                            <div className="mx-nav">
                                <button onClick={() => stepNav(-1)}>‹</button>
                                <span className="mx-period">{periodLabel}{singleAccolto ? ` · ${nomeById(singleAccolto)}` : ""}</span>
                                <button onClick={() => stepNav(1)}>›</button>
                            </div>
                            <div className="mx-head-right">
                                <button className="mx-oggi" onClick={goToday}>Oggi</button>
                                <div className="gran-toggle">
                                    <button className={gran === "mese" ? "active" : ""} onClick={() => setGran("mese")}>Mese</button>
                                    <button className={gran === "settimana" ? "active" : ""} onClick={() => setGran("settimana")}>Settimana</button>
                                    <button className={gran === "giorno" ? "active" : ""} onClick={() => setGran("giorno")}>Giorno</button>
                                </div>
                            </div>
                        </div>
                        <div className="panel-body">
                            {data.giorni.length === 0 ? (
                                <div className="mock-note">Nessun piano presenze pubblicato su {fonteLabel} per <strong>{meseLabel}</strong>.</div>
                            ) : (
                                <div className="matrix-scroll">
                                    <table className={`mx-table mx-g-${gran}`}>
                                        <thead>
                                            <tr>
                                                <th className="mx-name-col">Accolto</th>
                                                <th className="mx-tot-col" title="Presenze / giorni previsti nel periodo mostrato — passa il mouse su un valore per il dettaglio">Tot</th>
                                                {visDays.map((d) => {
                                                    const wd = GIORNI[(new Date(anno, mese - 1, d).getDay() + 6) % 7];
                                                    const we = wd === "Sab" || wd === "Dom";
                                                    return <th key={d} className={`mx-day${we ? " mx-weekend" : ""}${isOggi(d) ? " mx-today" : ""}`}><span className="mx-dnum">{d}</span><span className="mx-dwd">{wd}</span></th>;
                                                })}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {matrixAccolti.map((acc) => (
                                                <tr key={acc.id} className={hlRow === acc.id ? "mx-row-hl" : ""} onClick={() => setHlRow((prev) => (prev === acc.id ? null : acc.id))}>
                                                    {/* title: il nome e' troncato con l'ellipsis, qui resta leggibile per intero */}
                                                    <td className="mx-name-col" title={acc.nome}>{acc.nome}</td>
                                                    {/* Tot: presenze/previsti. onClick oltre al mouseenter perche' su touch l'hover non esiste */}
                                                    <td
                                                        className="mx-tot-col"
                                                        onMouseEnter={(e) => openTotTip(e, acc)}
                                                        onMouseLeave={() => setTotTip(null)}
                                                        onClick={(e) => { e.stopPropagation(); if (totTip && totTip.id === acc.id) setTotTip(null); else openTotTip(e, acc); }}
                                                    >
                                                        <span className={`mx-tot-chip ${heatClass(statsById[acc.id].presenze, statsById[acc.id].assenze) || "tot-na"}`}>
                                                            {statsById[acc.id].presenze}/{statsById[acc.id].previsti}
                                                        </span>
                                                    </td>
                                                    {visDays.map((d) => {
                                                        const ds = `${anno}-${mm}-${String(d).padStart(2, "0")}`;
                                                        const g = giorniByDate[ds];
                                                        // cerco l'accolto anche sui giorni chiusi, per far emergere le presenze extra
                                                        const a = g ? g.accolti.find((x) => x.id === acc.id) : null;
                                                        if (!g || (!g.chiusura && !a)) {
                                                            // Cella vuota = accolto non nel piano di quel giorno: si può
                                                            // inserire a mano (es. contratto caricato dopo), mai nel futuro.
                                                            if (ds > oggiStr) return <td key={d} className="mx-cell mx-empty" />;
                                                            return (
                                                                <td key={d} className="mx-cell mx-empty mx-addable" title="Inserisci presenza a mano"
                                                                    onClick={(e) => { e.stopPropagation(); setHlRow(acc.id); apriInserimento(acc.id, ds); }}>
                                                                    <span className="mx-add">+</span>
                                                                </td>
                                                            );
                                                        }
                                                        let cls, txt, serv = "", isExtra = false;
                                                        const isManuale = !!(a && a.manuale);
                                                        if (fuoriChiusura(a)) { // extra o manuale: ha priorità sul "C" di un giorno chiuso
                                                            isExtra = !!a.extra;
                                                            if (a.esito === "assente") { cls = "mx-a"; txt = "A"; }
                                                            else { cls = a.presenza_mezza ? "mx-pm" : "mx-p"; txt = "P"; serv = a.serviziEffettuati.map((s) => TRASP_LET[s]).join(" "); }
                                                        }
                                                        else if (g.chiusura) { cls = "mx-c"; txt = "C"; }
                                                        else if (a.esito === "na") { cls = "mx-na"; txt = "·"; }
                                                        else if (a.esito === "assente") { cls = "mx-a"; txt = "A"; }
                                                        else { cls = a.presenza_mezza ? "mx-pm" : "mx-p"; txt = "P"; serv = a.serviziEffettuati.map((s) => TRASP_LET[s]).join(" "); }
                                                        return (
                                                            <td key={d} className="mx-cell" onClick={(e) => { e.stopPropagation(); setHlRow(acc.id); setCellModal({ accId: acc.id, ds }); }}>
                                                                {isExtra && <span className="mx-extra" title="Presenza extra (giorno chiuso)">★</span>}
                                                                {isManuale && <span className="mx-manuale" title={`Inserita a mano${a.operatore ? ` da ${a.operatore}` : ""}`}>✎</span>}
                                                                <div className="mx-chip">
                                                                    <div className={`mx-status ${cls}`}>{txt}</div>
                                                                    {serv && <div className="mx-serv">{serv}</div>}
                                                                </div>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <div className="legend">
                                <span><span className="swatch" style={{ background: "#11B981" }} /> P = Presente</span>
                                <span><span className="swatch" style={{ background: "#E8C766" }} /> P mezza giornata</span>
                                <span><span className="swatch" style={{ background: "#EF4444" }} /> A = Assente</span>
                                <span><span className="swatch" style={{ background: "#E2E8F0" }} /> C = Chiuso</span>
                                <span><span className="swatch" style={{ background: "#C9B6E4" }} /> A/R/I = trasporti sotto la cella</span>
                                <span><span className="swatch" style={{ background: "#F59E0B" }} /> ★ = presenza extra (giorno chiuso)</span>
                                <span><span className="swatch" style={{ background: "#6366F1" }} /> ✎ = inserita a mano · clic su una cella vuota per inserire</span>
                                <span><b>Tot</b> = presenze / giorni previsti nel periodo mostrato (dettaglio al passaggio del mouse)</span>
                            </div>
                        </div>
                    </div>
                )}
                </div>

                {/* GRAFICI */}
                {data.giorni.length > 0 && (
                    <>
                        <div className="charts">
                            <div className="panel"><div className="panel-body">
                                <div className="chart-title">Andamento presenze / assenze ({MESI[mese - 1]})</div>
                                {trend.length === 0 ? <p className="cal-na">Nessun giorno aperto</p> : (
                                    <div className="bars">
                                        {trend.map((t) => {
                                            const ph = Math.round((t.p / trendMax) * 130);
                                            const ah = Math.round((t.a / trendMax) * 130);
                                            const wd = GIORNI[(new Date(t.data + "T00:00:00").getDay() + 6) % 7];
                                            return (
                                                <div
                                                    className="bar-col"
                                                    key={t.data}
                                                    onMouseEnter={(e) => { const r = e.currentTarget.getBoundingClientRect(); setBarTip({ x: r.left + r.width / 2, y: r.top - 10, t }); }}
                                                    onMouseLeave={() => setBarTip(null)}
                                                >
                                                    <div className="bar-stack" style={{ height: ph + ah }}>
                                                        <div className="bar-ass" style={{ height: ah }} />
                                                        <div className="bar-pres" style={{ height: ph }} />
                                                    </div>
                                                    <div className="bar-x">{wd} {t.data.slice(8, 10)}</div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div></div>
                            <div className="panel"><div className="panel-body">
                                <div className="chart-title">Top assenti nel periodo</div>
                                {topAssenti.length === 0 ? <p className="cal-na">Nessuna assenza registrata</p> : (
                                    <div className="hbars">
                                        {topAssenti.map((t) => (
                                            <div className="hbar-row" key={t.nome}>
                                                <span>{t.nome}</span>
                                                <div className="hbar-track"><div className="hbar-fill" style={{ width: `${(t.assenze / topMax) * 100}%` }} /></div>
                                                <b>{t.assenze}</b>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div></div>
                        </div>
                        <div className="charts">
                            <div className="panel"><div className="panel-body">
                                <div className="chart-title">Trasporti erogati per tipo</div>
                                <div className="donut-wrap">
                                    <svg width="120" height="120" viewBox="0 0 42 42">
                                        <circle cx="21" cy="21" r="15.9" fill="none" stroke="#eef2f7" strokeWidth="6" />
                                        {trasportiSeg.filter((s) => s.value > 0).map((s, i) => (
                                            <circle key={i} cx="21" cy="21" r="15.9" fill="none" stroke={s.color} strokeWidth="6" strokeDasharray={s.dasharray} strokeDashoffset={s.dashoffset} />
                                        ))}
                                    </svg>
                                    <div className="donut-legend">
                                        {trasportiSeg.map((s) => <span key={s.label}><span className="swatch" style={{ background: s.color }} /> {s.label} — {s.value}</span>)}
                                    </div>
                                </div>
                            </div></div>
                            <div className="panel"><div className="panel-body">
                                <div className="chart-title">Motivi di chiusura</div>
                                <div className="donut-wrap">
                                    <svg width="120" height="120" viewBox="0 0 42 42">
                                        <circle cx="21" cy="21" r="15.9" fill="none" stroke="#eef2f7" strokeWidth="6" />
                                        {chiusureSeg.map((s, i) => (
                                            <circle key={i} cx="21" cy="21" r="15.9" fill="none" stroke={s.color} strokeWidth="6" strokeDasharray={s.dasharray} strokeDashoffset={s.dashoffset} />
                                        ))}
                                    </svg>
                                    <div className="donut-legend">
                                        {chiusureSeg.length === 0 ? <span style={{ color: "var(--text-muted)" }}>Nessuna chiusura nel periodo</span>
                                            : chiusureSeg.map((s) => <span key={s.label}><span className="swatch" style={{ background: s.color }} /> {s.label} — {s.value}</span>)}
                                    </div>
                                </div>
                            </div></div>
                        </div>
                    </>
                )}
            </>
        );
    };

    // ---- MODALE dettaglio CELLA (matrice): singolo accolto in un giorno ----
    // Due modi: lettura (come prima) e form di inserimento/modifica manuale (manForm).
    const renderCellModal = () => {
        if (!cellModal || !data) return null;
        const g = data.giorni.find((x) => x.data === cellModal.ds) || null;
        // un giorno senza post si apre solo per inserire a mano
        if (!g && !manForm) return null;
        const nome = data.accoltiList.find((a) => a.id === cellModal.accId)?.nome || cellModal.accId;
        const dt = new Date(cellModal.ds + "T00:00:00");
        const dateLabel = `${GIORNI[(dt.getDay() + 6) % 7]} ${cellModal.ds.slice(8, 10)}/${cellModal.ds.slice(5, 7)}/${cellModal.ds.slice(0, 4)}`;
        const a = g ? g.accolti.find((x) => x.id === cellModal.accId) || null : null;
        const isExtra = !!(a && a.extra);
        const isManuale = !!(a && a.manuale);
        const fuori = fuoriChiusura(a);
        const esito = manForm ? manForm.stato : fuori ? a.esito : (g.chiusura ? "chiuso" : a ? a.esito : "na");
        const mezza = !manForm && a && a.presenza_mezza;
        const iconBg = esito === "presente" ? (mezza ? "#E8C766" : "#11B981") : esito === "assente" ? "#EF4444" : esito === "chiuso" ? "#94A3B8" : "#CBD5E1";
        const has = (s) => !!(a && a.serviziEffettuati.includes(s));
        const trRow = (label, key) => (
            <div className="cd-t" key={key}><span>{label}</span><span className={has(key) ? "ok" : "no"}>{has(key) ? "✓ effettuato" : "—"}</span></div>
        );

        const toggleServizio = (k) => setManForm((f) => ({
            ...f,
            servizi: f.servizi.includes(k) ? f.servizi.filter((x) => x !== k) : [...f.servizi, k],
        }));

        const renderForm = () => (
            <>
                <div className="cd-note">
                    {manForm.nuovo
                        ? "L'accolto non è nel piano di questo giorno (ad esempio perché il contratto è stato caricato dopo la pubblicazione). La presenza viene salvata come inserita a mano."
                        : isManuale
                            ? "Modifica della presenza inserita a mano."
                            : "Correzione di una presenza registrata dall'operatore dall'app. La correzione vive solo su questa dashboard: non viene sincronizzata su Interacta."}
                </div>
                <div className="cd-section-title">Stato</div>
                <div className="cd-choice">
                    {["presente", "assente"].map((st) => (
                        <button
                            type="button"
                            key={st}
                            className={`cd-choice-btn${manForm.stato === st ? ` active ch-${st}` : ""}`}
                            // un assente non viene trasportato: si azzerano i trasporti
                            onClick={() => setManForm((f) => ({ ...f, stato: st, servizi: st === "assente" ? [] : f.servizi }))}
                        >
                            {ESITO_LABEL[st]}
                        </button>
                    ))}
                </div>
                <div className="cd-section-title">Trasporti effettuati</div>
                <div className="cd-transp">
                    {TRASPORTI_FORM.map(([k, label]) => (
                        <label key={k} className={`cd-t cd-check${manForm.stato === "assente" ? " disabled" : ""}`}>
                            <span>{label}</span>
                            <input type="checkbox" checked={manForm.servizi.includes(k)} disabled={manForm.stato === "assente"} onChange={() => toggleServizio(k)} />
                        </label>
                    ))}
                </div>
                {manForm.errore && <div className="cd-error">{manForm.errore}</div>}
                <div className="cd-actions">
                    <button type="button" className="cd-btn cd-btn-secondary" disabled={manForm.saving}
                        onClick={manForm.nuovo ? chiudiCellModal : () => setManForm(null)}>
                        Annulla
                    </button>
                    <button type="button" className="cd-btn cd-btn-primary" disabled={manForm.saving} onClick={salvaManuale}>
                        {manForm.saving ? "Salvataggio…" : "Salva"}
                    </button>
                </div>
            </>
        );

        const renderLettura = () => (
            g.chiusura && !fuori ? (
                <div className="cd-note">Centro chiuso{g.motivo_chiusura ? ` — ${g.motivo_chiusura}` : ""}.</div>
            ) : (
                <>
                    {isExtra && (
                        <div className="cd-row"><span className="cd-label">Tipo</span><span className="pill pill-extra">★ Presenza extra{g.chiusura ? " (giorno chiuso)" : ""}</span></div>
                    )}
                    {isManuale && (
                        <div className="cd-row"><span className="cd-label">Tipo</span><span className="pill pill-manuale">✎ Inserita a mano{a.operatore ? ` da ${a.operatore}` : ""}</span></div>
                    )}
                    {!isManuale && !isExtra && a && a.operatore && (
                        <div className="cd-row"><span className="cd-label">Tipo</span><span className="pill pill-operatore">Registrata da {a.operatore}</span></div>
                    )}
                    <div className="cd-row"><span className="cd-label">Stato</span><span className={`pill pill-${ESITO_LETTERA[esito] || "na"}`}>{ESITO_LABEL[esito]}</span></div>
                    {esito === "presente" && (
                        <div className="cd-row"><span className="cd-label">Presenza</span><span>{a.presenza_mezza ? "Mezza giornata" : "Intera"}</span></div>
                    )}
                    <div className="cd-section-title">Trasporti effettuati</div>
                    <div className="cd-transp">
                        {TRASPORTI_FORM.map(([k, label]) => trRow(label, k))}
                    </div>
                    {!isManuale && (
                        <>
                            <div className="cd-section-title">Nota operatore</div>
                            <div className="cd-note">{a && a.nota ? a.nota : "Nessuna nota registrata"}</div>
                        </>
                    )}
                    {a && (
                        <div className="cd-actions">
                            {isManuale && (
                                <button type="button" className="cd-btn cd-btn-danger" onClick={eliminaManuale}>Elimina</button>
                            )}
                            <button type="button" className="cd-btn cd-btn-primary" onClick={() => apriModifica(a)}>Modifica</button>
                        </div>
                    )}
                </>
            )
        );

        return (
            <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !(manForm && manForm.saving)) chiudiCellModal(); }}>
                <div className="modal cd-modal">
                    <div className="modal-header">
                        <div className="m-icon" style={{ background: iconBg }}>{ESITO_LETTERA[esito] || "·"}</div>
                        <div style={{ flex: 1 }}>
                            <h3>{nome}</h3>
                            <p className="m-sub">{dateLabel}{manForm ? (manForm.nuovo ? " · inserimento manuale" : isManuale ? " · modifica manuale" : " · correzione presenza operatore") : ""}</p>
                        </div>
                        <button className="modal-close" onClick={chiudiCellModal} disabled={!!(manForm && manForm.saving)}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                        </button>
                    </div>
                    <div className="modal-body">
                        {manForm ? renderForm() : renderLettura()}
                        {!manForm && (
                            <div className="cd-legend">
                                <div className="cd-legend-title">Legenda</div>
                                <div className="cd-legend-grid">
                                    <span><span className="cd-badge mx-p">P</span> Presente</span>
                                    <span><span className="cd-badge mx-a">A</span> Assente</span>
                                    <span><span className="cd-badge mx-pm">P</span> Mezza giornata</span>
                                    <span><span className="cd-badge mx-c">C</span> Chiuso</span>
                                    <span><span className="cd-badge mx-serv">A</span> Andata (trasporto)</span>
                                    <span><span className="cd-badge mx-serv">R</span> Ritorno (trasporto)</span>
                                    <span><span className="cd-badge mx-serv">I</span> Individualizzato</span>
                                    <span><span className="mini-badge mb-daconf">da confermare</span> giornata non chiusa</span>
                                    <span><span style={{ color: "#F59E0B", fontWeight: 700 }}>★</span> Presenza extra (giorno chiuso)</span>
                                    <span><span style={{ color: "#6366F1", fontWeight: 700 }}>✎</span> Inserita a mano</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // ---- MODALE dettaglio giornata ----
    const renderDayModal = () => {
        if (!dayModal || !data) return null;
        const g = data.giorni.find((x) => x.data === dayModal);
        if (!g) return null;
        const dt = new Date(dayModal + "T00:00:00");
        const wd = GIORNI[(dt.getDay() + 6) % 7];
        const [Y, M, D] = dayModal.split("-");
        const label = `${wd} ${D}/${M}/${Y}`;
        const meta = g.senzaPiano
            ? `Senza piano · solo presenze inserite a mano · ${g.previstiCount} accolti`
            : g.confermato
                ? `Aperto · confermato${g.confermato_da ? ` da ${g.confermato_da}` : ""} · ${g.previstiCount} accolti`
                : `Aperto · ⚠ da confermare · ${g.previstiCount} accolti`;
        const accolti = filterAccolti(g.accolti);
        return (
            <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setDayModal(null); }}>
                <div className="modal">
                    <div className="modal-header">
                        <div className="m-icon">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                        </div>
                        <div style={{ flex: 1 }}>
                            <h3>{label}</h3>
                            <p className="m-sub">{meta}</p>
                        </div>
                        <button className="modal-close" onClick={() => setDayModal(null)}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                        </button>
                    </div>
                    <div className="modal-body">
                        {accolti.map((a) => {
                            const tr = a.serviziEffettuati.map((s) => TRASP_LET[s]).join(" ");
                            const lett = ESITO_LETTERA[a.esito] || "na";
                            return (
                                <div className="acc-row" key={a.id}>
                                    <div className="acc-name">
                                        {a.nome}
                                        {a.manuale && <span className="acc-manuale" title={`Inserita a mano${a.operatore ? ` da ${a.operatore}` : ""}`}> ✎</span>}
                                        {(tr || a.nota) && <small>{tr ? `trasporti: ${tr}` : ""}{tr && a.nota ? " · " : ""}{a.nota || ""}</small>}
                                    </div>
                                    <span className={`pill pill-${lett}`}>{ESITO_LABEL[a.esito]}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="gestione-presenze-container">
            <header className="page-header">
                <div className="header-content">
                    <button className="back-button" onClick={() => navigate("/produttivita")}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                        Indietro
                    </button>
                    <div className="header-title">
                        <div className="header-title-row">
                            <h2>Gestione Presenze</h2>
                            <button className="info-button-modern" onClick={() => setShowInfo(true)} title="Informazioni">
                                <span className="info-icon-text">i</span>
                            </button>
                        </div>
                        <p className="header-subtitle">{CENTRI[centro].subtitle}</p>
                    </div>
                </div>
            </header>

            {showInfo && (
                <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowInfo(false); }}>
                    <div className="modal">
                        <div className="modal-header">
                            <div className="m-icon">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
                            </div>
                            <div style={{ flex: 1 }}><h3>Informazioni sulla pagina</h3></div>
                            <button className="modal-close" onClick={() => setShowInfo(false)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="gp-info-section">
                                <h4>A cosa serve</h4>
                                <p>Incrocia il <strong>previsto</strong> (piano giornaliero pubblicato su Interacta{data?.centro ? ` community ${data.centro.communityId}` : ""}) con il <strong>consuntivo</strong> registrato dagli operatori sull'app. Un'<strong>assenza</strong> = previsto presente + consuntivo assente; la <strong>chiusura</strong> non è un'assenza (esclusa dai tassi).</p>
                            </div>
                            <div className="gp-info-section">
                                <h4>Centri</h4>
                                <p>I tab <strong>CSRD</strong> e <strong>CSO</strong> sono due centri diurni distinti, con anagrafiche, piani e consuntivi separati. Il <strong>periodo</strong> selezionato si conserva passando da un tab all'altro (per confrontarli); il filtro per <strong>accolto</strong> no, perché gli accolti dei due centri sono persone diverse.</p>
                            </div>
                            <div className="gp-info-section">
                                <h4>Presenze inserite a mano e correzioni</h4>
                                <p>Se un contratto viene caricato <strong>dopo</strong> la pubblicazione del piano, l'accolto non compare nel piano di quei giorni e l'operatore non lo vede nell'app. In vista <strong>Dettaglio</strong> clicca la sua <strong>cella vuota</strong> (giorni passati o oggi) e registra presenza/assenza e trasporti: la cella viene segnata con <strong>✎</strong>. Le presenze inserite a mano si possono modificare o eliminare. Le presenze registrate dall'operatore si possono <strong>correggere</strong> (es. un trasporto selezionato per errore) ma non eliminare da qui: la correzione resta solo su questa dashboard, non viene riportata su Interacta.</p>
                            </div>
                            <div className="gp-info-section">
                                <h4>Filtri e viste</h4>
                                <p>Scegli <strong>Periodo</strong> e uno o più <strong>Accolti</strong> nel pannello filtri e premi <strong>Applica</strong>. <strong>Dettaglio</strong> = matrice accolti × giorni (drill-down mese/settimana/giorno); <strong>Aggregato</strong> = riepilogo per giorno (o P/A/C del singolo accolto), clicca un giorno per la scheda. Il badge <strong>"da confermare"</strong> indica una giornata non ancora chiusa dall'operatore.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="content-area">
                {/* Tab centro. Sta fuori dai rami loading/error di proposito: se un
                    centro non carica si deve poter tornare all'altro. */}
                <div className="tabs">
                    {Object.entries(CENTRI).map(([key, c]) => (
                        <button
                            key={key}
                            className={`tab ${centro === key ? "active" : ""}`}
                            onClick={() => changeCentro(key)}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-5h6v5" />
                            </svg>
                            {c.label}
                        </button>
                    ))}
                </div>

                {loading && (
                    <Lf23Loader testo="Caricamento presenze in corso..." />
                )}
                {!loading && error && (
                    <div className="gp-state gp-error">
                        <p>Errore nel caricamento: {error}</p>
                        <button className="filter-reset-btn" onClick={fetchData}>Riprova</button>
                    </div>
                )}
                {!loading && !error && data && renderContent()}
            </div>

            {renderDayModal()}
            {renderCellModal()}

            {barTip && (() => {
                const dt = new Date(barTip.t.data + "T00:00:00");
                const wd = GIORNI[(dt.getDay() + 6) % 7];
                const [Y, M, D] = barTip.t.data.split("-");
                const tot = barTip.t.p + barTip.t.a;
                const tasso = tot > 0 ? Math.round((barTip.t.p / tot) * 100) : 0;
                return (
                    <div className="gp-bar-tooltip" style={{ position: "fixed", left: barTip.x, top: barTip.y, transform: "translate(-50%, -100%)", zIndex: 10000 }}>
                        <div className="tt-date">{wd} {D}/{M}/{Y}</div>
                        <div className="tt-row"><span className="tt-dot tt-pres" /> Presenti <b>{barTip.t.p}</b></div>
                        <div className="tt-row"><span className="tt-dot tt-ass" /> Assenti <b>{barTip.t.a}</b></div>
                        <div className="tt-sub">{tot} previsti · tasso presenza {tasso}%</div>
                    </div>
                );
            })()}

            {/* Dettaglio della colonna "Tot": stesso vestito del tooltip delle barre */}
            {totTip && (
                <div className="gp-bar-tooltip gp-tot-tooltip" style={{ position: "fixed", left: totTip.x, top: totTip.y, transform: "translate(-50%, -100%)", zIndex: 10000 }}>
                    <div className="tt-date">{totTip.nome}</div>
                    <div className="tt-per">{totTip.periodo}</div>
                    <div className="tt-row"><span className="tt-dot tt-pres" /> Presenze <b>{totTip.s.presenze}</b></div>
                    <div className="tt-row"><span className="tt-dot tt-ass" /> Assenze <b>{totTip.s.assenze}</b></div>
                    <div className="tt-row"><span className="tt-dot tt-mezz" /> Mezze giornate <b>{totTip.s.mezze}</b></div>
                    <div className="tt-row"><span className="tt-dot tt-tra" /> Trasporti <b>A {totTip.s.andata} · R {totTip.s.ritorno}{totTip.s.indiv ? ` · I ${totTip.s.indiv}` : ""}</b></div>
                    <div className="tt-sub">
                        {totTip.s.previsti} giorni previsti · tasso presenza {totTip.s.tasso}%
                        {totTip.s.chiusi > 0 ? ` · ${totTip.s.chiusi} ${totTip.s.chiusi === 1 ? "chiusura" : "chiusure"}` : ""}
                        {totTip.s.presenzeExtra > 0 ? ` · ${totTip.s.presenzeExtra} extra` : ""}
                    </div>
                </div>
            )}
        </div>
    );
};

export default GestionePresenze;
